/**
 * Tie-Dye Order Form — Native Fields (Hulk-free)
 * PTA Shopify Store
 *
 * WHAT THIS DOES:
 *   Reads the grade/teacher rosters from tie-dye-teacher-config.liquid
 *   (rendered as small JSON blobs sourced from product metafields) and
 *   builds native form fields:
 *     - Student First Name (text input)
 *     - Student Last Name (text input)
 *     - Grade Level (radio buttons, auto-ordered PreK -> highest grade)
 *     - Teacher Name (radio buttons, filtered to the selected grade)
 *   All fields submit as Shopify line-item properties, so they show
 *   up on the order and in Data Export IO under these exact names:
 *     properties[Student First Name]
 *     properties[Student Last Name]
 *     properties[Grade Level]
 *     properties[Teacher Name]
 *
 * WHAT TO UPDATE EACH YEAR:
 *   Nothing in this file. Teacher rosters are edited entirely on the
 *   product's edit page, under "Product metafields."
 *
 * GRADE ORDERING / LABELS (see gradeLabel() and gradeSortValue() below):
 *   Both are derived automatically from each metafield's key (e.g.
 *   "1st_grade_teachers" -> label "1st Grade", sorts after "prek" and
 *   "kindergarten" by its leading digit). PreK and Kindergarten are
 *   special-cased since they don't start with a number. A brand-new
 *   grade that doesn't match this pattern (e.g. something non-numeric)
 *   will still appear, just pushed to the end of the grade list, until
 *   you add a case for it here.
 *
 * IF DATA EXPORT IO EXPECTS DIFFERENT PROPERTY NAMES:
 *   Update the four PROPERTY_NAMES values below to match.
 */

window.addEventListener('load', function () {

  const CONFIG = {
    debugParam: 'dmx-dev-qa',
    maxWaitMs: 10000,
    pollIntervalMs: 300
  };

  const PROPERTY_NAMES = {
    firstName: 'Student First Name',
    lastName: 'Student Last Name',
    grade: 'Grade Level',
    teacher: 'Teacher Name'
  };

  // Donation product variants - update these IDs if the donation product
  // is ever recreated (e.g. a new one for a future school year).
  const DONATION_TIERS = [
    { label: 'Donate 1 T-Shirt ($12)', variantId: '52608584024346' },
    { label: 'Donate 2 T-Shirts ($24)', variantId: '52608584057114' },
    { label: 'Donate 3 T-Shirts ($36)', variantId: '52608584089882' },
    { label: 'Donate 4 T-Shirts ($48)', variantId: '52608584122650' },
    { label: 'Donate 5 T-Shirts ($60)', variantId: '52608584155418' }
  ];

  function isDevQAEnabled() {
    return window.location.search.includes(CONFIG.debugParam);
  }

  function devQALog(message) {
    if (isDevQAEnabled()) {
      console.log(message);
    }
  }

  // Strips the "_teachers" suffix off a metafield key, e.g.
  // "1st_grade_teachers" -> "1st_grade", "prek_teachers" -> "prek"
  function baseGradeName(gradeKey) {
    return gradeKey.replace(/_teachers$/, '');
  }

  // Derives a display label directly from the metafield key - no
  // hardcoded per-grade list to maintain. PreK and Kindergarten are
  // special-cased since they don't follow the "number + grade" pattern.
  function gradeLabel(gradeKey) {
    const base = baseGradeName(gradeKey);
    if (base === 'prek') return 'PreK';
    if (base === 'kindergarten') return 'Kindergarten';
    return base
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Derives sort position directly from the metafield key. PreK and
  // Kindergarten sort first (in that order); numbered grades sort by
  // their leading digit (numeric, not alphabetic, so "10th" would
  // correctly sort after "9th" rather than before "2nd"). Anything
  // that doesn't match a recognized pattern is pushed to the end.
  function gradeSortValue(gradeKey) {
    const base = baseGradeName(gradeKey);
    if (base === 'prek') return -2;
    if (base === 'kindergarten') return -1;
    const match = base.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 999;
  }

  function readGradeConfig() {
    const config = {};
    document.querySelectorAll('.tie-dye-grade-block').forEach(el => {
      const gradeKey = el.dataset.gradeKey;
      let teachers;
      try {
        teachers = JSON.parse(el.textContent);
      } catch (e) {
        devQALog(`Could not parse teacher list for "${gradeKey}"`);
        return;
      }
      if (!Array.isArray(teachers)) {
        devQALog(`Unexpected format for "${gradeKey}" - expected a list, got: ${typeof teachers}`);
        return;
      }
      teachers = teachers.map(t => (t || '').trim()).filter(Boolean);
      if (teachers.length > 0) {
        config[gradeKey] = teachers;
      }
    });
    return config;
  }

  function buildFieldsMarkup() {
    return `
      <div class="tie-dye-fields" style="margin: 16px 0; display: flex; flex-direction: column; gap: 16px; color: #1f1f1f;">
        <div class="tie-dye-field" style="border:2px solid transparent; border-radius:6px; padding:8px; box-sizing:border-box; margin-left:-8px; margin-right:-8px;">
          <label for="tie-dye-first-name" style="display:block; margin-bottom:6px; font-weight:600;">Student First Name</label>
          <input type="text" id="tie-dye-first-name" name="properties[${PROPERTY_NAMES.firstName}]"
                 style="width:100%; padding:8px 12px; border:1px solid #DADADA; border-radius:6px;">
        </div>

        <div class="tie-dye-field" style="border:2px solid transparent; border-radius:6px; padding:8px; box-sizing:border-box; margin-left:-8px; margin-right:-8px;">
          <label for="tie-dye-last-name" style="display:block; margin-bottom:6px; font-weight:600;">Student Last Name</label>
          <input type="text" id="tie-dye-last-name" name="properties[${PROPERTY_NAMES.lastName}]"
                 style="width:100%; padding:8px 12px; border:1px solid #DADADA; border-radius:6px;">
        </div>

        <div class="tie-dye-field" style="border:2px solid transparent; border-radius:6px; padding:8px; box-sizing:border-box; margin-left:-8px; margin-right:-8px;">
          <label style="display:block; margin-bottom:6px; font-weight:600;">Student Grade Level</label>
          <div class="tie-dye-grade-options" style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px;"></div>
        </div>

        <div class="tie-dye-field tie-dye-teacher-field" style="display:none; border:2px solid transparent; border-radius:6px; padding:8px; box-sizing:border-box; margin-left:-8px; margin-right:-8px;">
          <label style="display:block; margin-bottom:6px; font-weight:600;">Teacher</label>
          <div class="tie-dye-teacher-options" style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px;"></div>
        </div>

        <div class="tie-dye-field tie-dye-donation-callout" style="background:rgba(59,184,196,0.13); border:1px solid rgba(0,0,0,0.08); border-radius:20px; padding:24px; box-shadow:0 12px 32px rgba(0,0,0,0.045);">
          <div style="display:flex; align-items:flex-start; gap:14px; margin-bottom:14px;">
            <div style="flex-shrink:0; width:44px; height:44px; border-radius:14px; background:rgba(59,184,196,0.25); display:flex; align-items:center; justify-content:center; font-size:20px;">💛</div>
            <label style="display:block; color:#1f1f1f; font-weight:600; padding-top:4px;">Would you like to make a donation to ensure all Brentwood students have a tie-dye t-shirt this year? <span style="font-weight:normal; color:#666666;">(Please note that any donations that exceed community need will be moved into our Bulldog Fund.)</span></label>
          </div>
          <div class="tie-dye-donation-options" style="display:grid; grid-template-columns:repeat(3, 1fr); gap:8px;"></div>
        </div>
      </div>
    `;
  }

  function makeRadioOption(name, value, label) {
    // "product__chip" is the theme's own class for the Size selector
    // buttons - reusing it (plus toggling "selected" the same way the
    // theme does) keeps our fields visually identical to native chips
    // automatically, including if the theme's colors/fonts ever change.
    const wrapper = document.createElement('label');
    wrapper.className = 'tie-dye-radio-option product__chip';
    wrapper.style.cssText =
      'cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px; text-align:center; box-sizing:border-box; width:100%;';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = name;
    input.value = value;
    input.style.margin = '0';

    const span = document.createElement('span');
    span.textContent = label;

    wrapper.appendChild(input);
    wrapper.appendChild(span);
    return { wrapper, input };
  }

  function highlightSelected(container) {
    container.querySelectorAll('.tie-dye-radio-option').forEach(opt => {
      const input = opt.querySelector('input');
      opt.classList.toggle('selected', input.checked);
    });
  }

  function renderGradeOptions(gradeContainer, teacherFieldWrapper, teacherContainer, gradeConfig) {
    const gradeKeys = Object.keys(gradeConfig).sort(
      (a, b) => gradeSortValue(a) - gradeSortValue(b)
    );

    gradeKeys.forEach(gradeKey => {
      const label = gradeLabel(gradeKey);
      const { wrapper, input } = makeRadioOption(`properties[${PROPERTY_NAMES.grade}]`, label, label);
      input.addEventListener('change', function () {
        highlightSelected(gradeContainer);
        clearFieldError(gradeContainer.closest('.tie-dye-field'));
        renderTeacherOptions(teacherFieldWrapper, teacherContainer, gradeConfig[gradeKey]);
        devQALog(`Grade selected: ${label}`);
      });
      gradeContainer.appendChild(wrapper);
    });
  }

  function renderTeacherOptions(teacherFieldWrapper, teacherContainer, teachers) {
    teacherContainer.innerHTML = '';
    teachers.forEach(teacherName => {
      const { wrapper, input } = makeRadioOption(`properties[${PROPERTY_NAMES.teacher}]`, teacherName, teacherName);
      input.addEventListener('change', function () {
        highlightSelected(teacherContainer);
        clearFieldError(teacherFieldWrapper);
        devQALog(`Teacher selected: ${teacherName}`);
      });
      teacherContainer.appendChild(wrapper);
    });
    teacherFieldWrapper.style.display = teachers.length ? 'block' : 'none';
  }

  function renderDonationOptions(donationContainer) {
    const { wrapper: noneWrapper, input: noneInput } = makeRadioOption('tie-dye-donation-tier', '', 'No thanks');
    noneInput.checked = true;
    noneInput.required = false;
    noneInput.addEventListener('change', () => {
      highlightSelected(donationContainer);
      devQALog('Donation: none selected');
    });
    donationContainer.appendChild(noneWrapper);

    DONATION_TIERS.forEach(tier => {
      const { wrapper, input } = makeRadioOption('tie-dye-donation-tier', tier.variantId, tier.label);
      input.required = false;
      input.addEventListener('change', () => {
        highlightSelected(donationContainer);
        devQALog(`Donation tier selected: ${tier.label}`);
      });
      donationContainer.appendChild(wrapper);
    });

    highlightSelected(donationContainer);
  }

  function showFieldError(fieldEl, message) {
    fieldEl.style.borderColor = '#C0392B';

    let errorEl = fieldEl.querySelector('.tie-dye-error-message');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'tie-dye-error-message';
      errorEl.style.cssText = 'color:#C0392B; font-size:0.9em; margin-top:6px;';
      fieldEl.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }

  function clearFieldError(fieldEl) {
    fieldEl.style.borderColor = 'transparent';
    const errorEl = fieldEl.querySelector('.tie-dye-error-message');
    if (errorEl) errorEl.remove();
  }

  function validateFields(fieldsEl) {
    let firstInvalidEl = null;
    let isValid = true;

    const checks = [
      {
        fieldEl: fieldsEl.querySelector('#tie-dye-first-name').closest('.tie-dye-field'),
        test: () => fieldsEl.querySelector('#tie-dye-first-name').value.trim().length > 0,
        message: 'Please enter the student\'s first name.'
      },
      {
        fieldEl: fieldsEl.querySelector('#tie-dye-last-name').closest('.tie-dye-field'),
        test: () => fieldsEl.querySelector('#tie-dye-last-name').value.trim().length > 0,
        message: 'Please enter the student\'s last name.'
      },
      {
        fieldEl: fieldsEl.querySelector('.tie-dye-grade-options').closest('.tie-dye-field'),
        test: () => !!fieldsEl.querySelector('.tie-dye-grade-options input:checked'),
        message: 'Please select a grade level.'
      },
      {
        fieldEl: fieldsEl.querySelector('.tie-dye-teacher-options').closest('.tie-dye-field'),
        test: () => !!fieldsEl.querySelector('.tie-dye-teacher-options input:checked'),
        message: 'Please select a teacher.'
      }
    ];

    checks.forEach(({ fieldEl, test, message }) => {
      if (test()) {
        clearFieldError(fieldEl);
      } else {
        showFieldError(fieldEl, message);
        isValid = false;
        if (!firstInvalidEl) firstInvalidEl = fieldEl;
      }
    });

    if (firstInvalidEl) {
      firstInvalidEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return isValid;
  }

  function attachLiveErrorClearing(fieldsEl) {
    const firstNameInput = fieldsEl.querySelector('#tie-dye-first-name');
    const lastNameInput = fieldsEl.querySelector('#tie-dye-last-name');

    firstNameInput.addEventListener('input', () => {
      if (firstNameInput.value.trim().length > 0) {
        clearFieldError(firstNameInput.closest('.tie-dye-field'));
      }
    });

    lastNameInput.addEventListener('input', () => {
      if (lastNameInput.value.trim().length > 0) {
        clearFieldError(lastNameInput.closest('.tie-dye-field'));
      }
    });
  }

  function insertFieldsIntoForm(cartForm) {
    const container = document.createElement('div');
    container.innerHTML = buildFieldsMarkup();
    const fieldsEl = container.firstElementChild;

    const submitButton = cartForm.querySelector('[type="submit"]');
    if (submitButton) {
      // Walk up until we reach the ancestor that is a direct child of the form -
      // that's the only node insertBefore can safely use as a reference here.
      let anchor = submitButton;
      while (anchor.parentElement && anchor.parentElement !== cartForm) {
        anchor = anchor.parentElement;
      }
      if (anchor.parentElement === cartForm) {
        cartForm.insertBefore(fieldsEl, anchor);
        devQALog('Fields inserted before the submit button\'s containing block');
      } else {
        cartForm.appendChild(fieldsEl);
        devQALog('Could not find a direct-child anchor - appended fields to end of form as fallback');
      }
    } else {
      cartForm.appendChild(fieldsEl);
      devQALog('Submit button not found - appended fields to end of form as fallback');
    }
    return fieldsEl;
  }

  function hideMiniCartFlash() {
    // Confirmed via inspection: this theme's post-add-to-cart popup is
    // ".purchase-confirmation-popup". We target it specifically - NOT any
    // broad "cart-drawer" wildcard, since that previously also hid the
    // real persistent header cart icon/drawer used for normal navigation.
    const style = document.createElement('style');
    style.textContent = `
      .purchase-confirmation-popup,
      [data-purchase-confirmation-popup] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }
    `;
    document.head.appendChild(style);
    devQALog('Injected CSS to suppress the purchase-confirmation popup only');
  }

  function hideShippingNote() {
    const shippingLink = document.querySelector('a[href*="/policies/shipping-policy"]');
    if (shippingLink) {
      // Hide the whole line it sits in, not just the link itself
      const container = shippingLink.closest('p') || shippingLink.parentElement;
      if (container) {
        container.style.display = 'none';
        devQALog('Hid the shipping-calculated-at-checkout note');
      }
    } else {
      devQALog('Shipping policy note not found - nothing to hide');
    }
  }

  function initializeScript() {
    const cartForm = document.querySelector('form[action="/cart/add"]');
    if (!cartForm) {
      devQALog('Cart form not found.');
      return;
    }

    const gradeConfig = readGradeConfig();
    if (Object.keys(gradeConfig).length === 0) {
      devQALog('No grade/teacher config found - check the product\'s "Teachers" metafields.');
      return;
    }
    devQALog(`Loaded grade config: ${JSON.stringify(gradeConfig)}`);

    const fieldsEl = insertFieldsIntoForm(cartForm);
    hideMiniCartFlash();
    const gradeContainer = fieldsEl.querySelector('.tie-dye-grade-options');
    const teacherFieldWrapper = fieldsEl.querySelector('.tie-dye-teacher-field');
    const teacherContainer = fieldsEl.querySelector('.tie-dye-teacher-options');
    const donationContainer = fieldsEl.querySelector('.tie-dye-donation-options');

    renderGradeOptions(gradeContainer, teacherFieldWrapper, teacherContainer, gradeConfig);
    renderDonationOptions(donationContainer);
    attachLiveErrorClearing(fieldsEl);
    hideShippingNote();

    // Intercept the button CLICK (not the form's submit event). This is
    // important: Shopify's theme has its own built-in submit handling that
    // performs its own add-to-cart call on the 'submit' event. Cancelling
    // preventDefault() on 'submit' does NOT stop that other handler from
    // already having run. Cancelling the click's default action instead
    // stops the browser from ever triggering a 'submit' event at all, so
    // nothing else gets a chance to add the item before our own validation
    // and fetch logic runs.
    const submitButton = cartForm.querySelector('[type="submit"]');
    if (!submitButton) {
      devQALog('Could not find the submit button - cannot attach add-to-cart handler.');
      return;
    }

    submitButton.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      if (!validateFields(fieldsEl)) {
        devQALog('Validation failed - not submitting');
        return;
      }

      devQALog('Add to Bag clicked - adding shirt (and donation, if selected)');

      const formData = new FormData(cartForm);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
        .then(response => {
          if (!response.ok) throw new Error('Failed to add shirt to cart');
          return response.json();
        })
        .then(() => {
          const selectedDonation = cartForm.querySelector('input[name="tie-dye-donation-tier"]:checked');
          if (selectedDonation && selectedDonation.value) {
            devQALog(`Adding donation variant ${selectedDonation.value} to cart`);
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: selectedDonation.value, quantity: 1 })
            }).then(response => {
              if (!response.ok) throw new Error('Failed to add donation to cart');
            });
          }
        })
        .then(() => {
          window.location.href = '/cart';
        })
        .catch(err => {
          devQALog(`Cart add failed: ${err.message}`);
          alert('Something went wrong adding this to your cart. Please try again.');
        });
    }, true); // capture phase, to run as early as possible
    devQALog('Add to Bag click handler added (shirt + optional donation)');
  }

  // The product form is native/server-rendered, so it should exist
  // immediately - but poll briefly in case the theme lazy-renders it.
  let waited = 0;
  const interval = setInterval(() => {
    const cartForm = document.querySelector('form[action="/cart/add"]');
    if (cartForm) {
      clearInterval(interval);
      initializeScript();
    } else {
      waited += CONFIG.pollIntervalMs;
      if (waited >= CONFIG.maxWaitMs) {
        clearInterval(interval);
        devQALog('Gave up waiting for the cart form.');
      }
    }
  }, CONFIG.pollIntervalMs);
});
